import React, { useState } from 'react';
import { useBrand } from '@/context/BrandContext';
import { EditableText } from '../ui/EditableText';
import { Upload } from 'lucide-react';
import { ImageUploadModal } from '../image-upload';
import { BrandSectionLayout } from './BrandSectionLayout';

interface BrandStorySectionProps {
  section: any;
  isEditing: boolean;
  isGlobalDarkMode: boolean;
  editingElement: string | null;
  setEditingElement: (element: string | null) => void;
  updateSectionContent: (key: string, value: string) => void;
  updateSectionStyles: (key: string, styles: any) => void;
  pageNumber?: number;
}

export const BrandStorySection: React.FC<BrandStorySectionProps> = ({
  section,
  isEditing,
  isGlobalDarkMode,
  editingElement,
  setEditingElement,
  updateSectionContent,
  updateSectionStyles,
  pageNumber = 1
}) => {
  const { state } = useBrand();
  const [showImageModal, setShowImageModal] = useState(false);

  const handleImageSelect = (imageUrl: string) => {
    updateSectionContent('storyImage', imageUrl);
    setShowImageModal(false);
  };

  return (
    <BrandSectionLayout
      section={section}
      isEditing={isEditing}
      isGlobalDarkMode={isGlobalDarkMode}
      editingElement={editingElement}
      setEditingElement={setEditingElement}
      updateSectionContent={updateSectionContent}
      updateSectionStyles={updateSectionStyles}
      pageNumber={pageNumber}
    >
      {/* Two-column layout with 2rem gap, left 50%, right 50% */}
      <div className="h-full grid grid-cols-2 gap-16">
        {/* Left Column - 50% width - Image */}
        <div
          className="relative group h-full"
          style={{
            margin: 0,
            padding: 0,
            overflow: isEditing ? 'visible' : 'hidden' // Allow overflow in edit mode for settings icons
          }}
        >
          {/* Image Container with medium border radius */}
          <div 
            className="w-full h-full relative rounded-md overflow-hidden"
            style={{
              margin: 0,
              padding: 0,
              borderRadius: '0.375rem' // 6px medium border radius
            }}
          >
            {/* Main Image with full container fill and medium border radius */}
            <img
              src={section.content.storyImage || '/img-placeholder-16-9.png'}
              alt="Brand Story Visual"
              className="w-full h-full object-cover rounded-md"
              style={{
                width: '100%',
                height: '100%',
                margin: 0,
                padding: 0,
                border: 'none',
                borderRadius: '0.375rem', // 6px medium border radius
                objectFit: 'cover',
                display: 'block'
              }}
              onError={(e) => {
                e.currentTarget.src = '/img-placeholder-16-9.png';
              }}

            />
            
            {/* Upload Overlay - Only visible in edit mode */}
            {isEditing && (
              <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity duration-200 flex items-center justify-center rounded-md z-10">
                <div className="text-center text-white">
                  <Upload className="w-8 h-8 mx-auto mb-2" />
                  <p className="text-sm font-medium mb-2">Change Image</p>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowImageModal(true);
                    }}
                    className="px-3 py-2 bg-white/90 text-black rounded text-sm hover:bg-white transition-colors"
                    style={{ zIndex: 20 }}
                  >
                    Upload New Image
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Right Column - 50% width - Content */}
        <div 
          className="flex flex-col justify-center" 
          style={{ 
            padding: 0,
            overflow: isEditing ? 'visible' : 'hidden' // Allow overflow in edit mode for settings icons
          }}
        >
          {/* Our Story Title */}
          <div className="mb-6">
            <EditableText
              value={section.content.storyTitle || 'Brand Story'}
              onChange={(value) => updateSectionContent('storyTitle', value)}
              element="h3"
              className="text-3xl leading-tight tracking-tight font-bold"
              placeholder="Brand Story"
              multiline={true}
              editModeEnabled={isEditing}
              isEditing={isEditing && editingElement === 'storyTitle'}
              onEditStart={() => setEditingElement('storyTitle')}
              onEditEnd={() => setEditingElement(null)}
              onStyleChange={(styles) => updateSectionStyles('storyTitle', styles)}
              initialStyles={{
                textTransform: 'capitalize',
                fontWeight: 'bold',
                lineHeight: 1.2,
                fontFamily: state.brandData?.globalTitleFont || 'Inter',
                ...section.content.storyTitleStyles
              }}
              isGlobalDarkMode={isGlobalDarkMode}
              sectionBackgroundColor={section.customization?.backgroundColor || section.styles.backgroundColor}
            />
          </div>

          {/* Brand Story Description */}
          <div>
            <EditableText
              value={section.content.brandStoryDescription || ''}
              onChange={(value) => updateSectionContent('brandStoryDescription', value)}
              element="p"
              className="text-lg leading-relaxed BrandStoryDescription"
              placeholder="Add Description"
              multiline={true}
              editModeEnabled={isEditing}
              isEditing={isEditing && editingElement === 'brandStoryDescription'}
              onEditStart={() => setEditingElement('brandStoryDescription')}
              onEditEnd={() => setEditingElement(null)}
              onStyleChange={(styles) => updateSectionStyles('brandStoryDescription', styles)}
              initialStyles={{
                fontFamily: state.brandData?.globalTextFont || 'Inter',
                ...section.content.brandStoryDescriptionStyles
              }}
              isGlobalDarkMode={isGlobalDarkMode}
              sectionBackgroundColor={section.customization?.backgroundColor || section.styles.backgroundColor}
            />
          </div>
        </div>
      </div>

      {/* Image Upload Modal */}
      <ImageUploadModal
        isOpen={showImageModal}
        onClose={() => setShowImageModal(false)}
        onImageSelect={handleImageSelect}
        isGlobalDarkMode={isGlobalDarkMode}
        title="Upload Brand Story Image"
        acceptedFormats={['.jpg', '.jpeg', '.png', '.gif', '.webp', '.svg']}
        maxFileSize={10}
        showUnsplash={true}
        initialSearchQuery="company story team"
      />
    </BrandSectionLayout>
  );
};